import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Chip,
  Divider,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  TextField,
  Typography,
} from '@mui/material';
import styles from './BaseSelect.module.scss';
import classNames from 'classnames/bind';
import { ArrowDownIcon, SearchIcon } from '~/assets/icons';
import { useTranslation } from 'react-i18next';

const cx = classNames.bind(styles);

export type BaseSelectProps<T extends Record<string, any>> = BoxProps &
  SelectProps & {
    value?: any;
    label?: string | undefined;
    title?: string | undefined;
    placeholder?: string | undefined;
    renderSelected?: (item: T) => React.ReactElement;
    onChange?: (...event: any[]) => void;
    options?: Array<T>;
    renderOptionItem?: (item: T, index: number) => React.ReactElement;
    menuClassName?: string;
    isSearchEnable?: boolean;
    isHideIcon?: boolean;
    bottomComponent?: () => React.ReactElement;
    topComponent?: () => React.ReactElement;
    helperText?: string;
  };

const BaseSelect = <T extends Record<string, any>>(props: BaseSelectProps<T>) => {
  //#region Destructuring Props
  const {
    isHideIcon = false,
    isSearchEnable = false,
    value,
    label,
    title,
    placeholder,
    renderSelected,
    onChange,
    options,
    className,
    multiple = false,
    ...restProps
  }: BaseSelectProps<T> = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/component');
  //#endregion Declare Hook

  //#region Declare State
  const [selectOptions, setSelectOptions] = useState<Array<T>>();
  const [searchValue, setSearchValue] = useState<string>('');
  //#endregion Declare State

  //#region Implement Hook
  const getFilterOption = useCallback((options: Array<T>, value: string) => {
    return options.filter((item) => {
      return item.name.toLowerCase().indexOf(value.toLowerCase()) > -1;
    });
  }, []);

  useEffect(() => {
    if (options?.length) {
      setSelectOptions(getFilterOption(options, searchValue));
    }
  }, [options, getFilterOption, searchValue]);
  //#endregion Implement Hook

  //#region Handle Function
  const renderOptionItem =
  props.renderOptionItem ||
  function (option: T, _index: number, isLast?: boolean) {
    return (
      <>
      <Box
        className={cx('select-option')}
      >
        {option.name}
      </Box>
      {!isLast && <Divider />}
      </>
    );
  };

  const defaultRenderSelectedItem = (selectedItem: any) => {
    if (Array.isArray(selectedItem)) {
      return (
        <Box className="p-[15px] flex gap-2 flex-wrap">
          {selectedItem.map((item) => (
            <Chip
              key={item.id}
              label={item.name}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      );
    }
    return <Box className="p-[15px]">{selectedItem?.name}</Box>;
  };

  const onSearch = (event: React.ChangeEvent) => {
    const value = (event.target as HTMLInputElement).value;
    setSearchValue(value);
    if (options?.length) {
      setSelectOptions(getFilterOption(options, value));
    }
  };
  //#endregion Handle Function

  // Remove invalid attribute
  delete restProps.menuClassName;
  delete restProps.renderOptionItem;
  delete restProps.topComponent;
  delete restProps.bottomComponent;
  delete restProps.helperText;

  return (
    <>
      {title && <InputLabel className={cx('form-input-title')}>{title}</InputLabel>}
      <Box className={cx('input-select-container', className)}>
        {selectOptions && (
          <Select
            multiple={multiple}
            value={value}
            onChange={onChange}
            renderValue={() => {
              if (value !== undefined) {
                let selectedItem: any = null;

                if (multiple && Array.isArray(value)) {
                  selectedItem = value
                    .map((val) => props.options?.find((item) => item.id === val))
                    .filter(Boolean);
                } else {
                  selectedItem = props.options?.find((item) => item.id === value);
                }

                if (selectedItem) {
                  if (renderSelected) {
                    if (Array.isArray(selectedItem)) {
                      return (
                        <Box className="p-[15px] flex gap-2 flex-wrap">
                          {selectedItem.map((it) => renderSelected(it))}
                        </Box>
                      );
                    }
                    return renderSelected(selectedItem);
                  }
                  return defaultRenderSelectedItem(selectedItem);
                }

                return (
                  <Box className="px-[15px] text-[#b0b4bd]">
                    {placeholder ?? label}
                  </Box>
                );
              }
              return (
                <Typography className="flex px-[15px] flex-1 select-none text-[14px]/[16.8px] text-[#A2A7B4] font-normal">
                  {placeholder ?? label ?? ''}
                </Typography>
              );
            }}
            defaultValue=""
            inputProps={{ 'aria-label': 'Without label' }}
            displayEmpty={true}
            IconComponent={
              isHideIcon
                ? 'span'
                : (props) => (
                    <ArrowDownIcon
                      {...props}
                      className={cx('dropdown-select-icon')}
                    />
                  )
            }
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
              id: 'select-menu',
              sx: { minWidth: 220 },
              className: cx('base-select-menu-item', props?.menuClassName),
            }}
            {...restProps}
          >
            {label && (
              <Typography className="mb-[16px] text-[14px]/[16.8px] text-[#3A3F51] font-medium">
                {label}
              </Typography>
            )}
            {isSearchEnable && (
              <TextField
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={t('baseSelect.search')}
                className="mb-[16px]"
                onChange={onSearch}
                defaultValue={searchValue}
                InputProps={{ startAdornment: <SearchIcon className="w-[16px]" /> }}
              />
            )}
            {props.topComponent && props.topComponent()}

            <MenuItem className="hidden" value=""></MenuItem>
            {selectOptions && renderOptionItem
              ? selectOptions.map((option: T, index: number) => {
                const isLast = index === selectOptions.length - 1;
                  return (
                    <MenuItem
                      key={index}
                      value={option.id}
                      // className={` ${isLast ? '' : 'border-b-[1px] border-solid border-b-[#ccc]'}`}
                    >
                      {renderOptionItem(option, index, isLast)}
                    </MenuItem>
                    
                  );
                })
              : restProps.children}
            {props.bottomComponent && props.bottomComponent()}
          </Select>
        )}
      </Box>
      {props.helperText && (
        <Box>
          <FormHelperText error={true}>{props.helperText}</FormHelperText>
        </Box>
      )}
    </>
  );
};

export default memo(BaseSelect) as typeof BaseSelect;
